import React, { useCallback, useEffect, useState } from "react";
import { Form, Layout, Button, Collapse, Typography, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import InventoryLayOutCommon from "../LayOut/InventoryLayOutCommon";
import AppPageHeader from "../../../common/PageHeader";
import InventoryForm from "./InventoryForm";
import ItemTable from "../ItemTable";
import {
  onGetBuyingOfficeDetails,
  onGetBuyingOfficeList,
  onGetStyleList,
  onResetStyleDetails,
  onResetBOM,
} from "../../../redux/actions";
import { onCreateInventory } from "../../../redux/actions";
import HandleNotification from "../../../common/Notification";
import {
  CHALLAN_LIST,
  GET_STYLE_LIST_ENDPOINT,
  INVOICE_BY_SUPPLIER,
  TRIMS_ACCESSORIES_ITEMS_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";

const CreateTrimsInventory = (props) => {
  const [value, setValue] = useState(null);
  const [originalPO, setOriginalPO] = useState("");
  const [internalPO, setInternalPO] = useState("");
  const [onEdit, setEdit] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [invoiceList, setInvoiceList] = useState(null);
  const [challanList, setChallanList] = useState(null);
  const [selectedSupplierType, setSelectedSupplierType] = useState(null);
  const [selectedBom, setSelectedBom] = useState(null);
  const [styleDetails, setStyleDetails] = useState(null);
  const [showItemTable, setShowItemTable] = useState(false);

  const dispatch = useDispatch();
  const styleList = useSelector(({ style }) => style.styleList);
  const loading = useSelector(({ common }) => common.loading);

  const location = useLocation();
  const selectedInventory = location.state;

  const [form] = Form.useForm();
  const { Content } = Layout;
  const { Panel } = Collapse;
  const { Title } = Typography;

  const dateFormat = "YYYY-MM-DD";

  useEffect(() => {
    dispatch(
      onGetBuyingOfficeList({
        page: 1,
        per_page: 10,
      }),
    );
    dispatch(onGetStyleList(1, 10));
    form.resetFields();

    return () => {
      dispatch(onResetStyleDetails());
      dispatch(onResetBOM());
    };
  }, [dispatch, form]);

  const resetStyleDependentState = useCallback(() => {
    form.resetFields([
      "supplier_id",
      "invoice_id",
      "challan_id",
      "reference_no",
      "requisition_no",
      "movement_type_incoming",
      "movement_type_outgoing",
      "internal_po",
      "original_po",
    ]);

    setSelectedSupplierId(null);
    setSelectedSupplierType(null);
    setInvoiceList(null);
    setSelectedBom(null);
    setShowItemTable(false);
    setValue(null);
    setOriginalPO("");
    setInternalPO("");
  }, [form]);

  const getStyleDetails = async (styleId) => {
    const query = `${GET_STYLE_LIST_ENDPOINT}/${styleId}/edit`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      const styleData = response?.data?.data?.[0];
      setStyleDetails(styleData);
    } else {
      HandleNotification("error", "bottomRight", "Error getting style details", null);
    }
  };

  useEffect(() => {
    if (styleDetails) {
      form.setFieldsValue({
        buying_office_id: styleDetails?.style?.buying_office_info?.id,
        buying_office_name: styleDetails?.style?.buying_office_info?.name,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        sales_contract_name:
          styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.reference_no,
        buyer_id: styleDetails?.style?.buyer_info?.id,
        buyer_name: styleDetails?.style?.buyer_info?.name,
        brand_id: styleDetails?.style?.brand_info?.id,
        brand_name: styleDetails?.style?.brand_info?.name,
        order_quantity: styleDetails?.style?.active_sales_contract_map_info?.order_quantity
          ? parseInt(styleDetails?.style?.active_sales_contract_map_info?.order_quantity)
          : 0,
      });
    }

    if (styleDetails?.style?.factory_info) {
      form.setFieldsValue({
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id,
        sub_contract_factory_name: styleDetails?.style?.factory_info?.name,
      });
    }
  }, [form, styleDetails]);

  const onChangeStyle = (value, options) => {
    getStyleDetails(value);
  };

  const BOMDetails = useCallback(async (props) => {
    const {
      buying_office_id,
      entry_date,
      movement_type,
      sales_contract_id,
      style_id,
      supplier_id,
      use_fabric_interlining_store,
      sub_contract_factory_id,
    } = props;
    const query = `${TRIMS_ACCESSORIES_ITEMS_LIST}`;
    const payload = {
      buying_office_id,
      entry_date,
      movement_type,
      sales_contract_id,
      style_id,
      supplier_id,
      use_fabric_interlining_store,
      sub_contract_factory_id,
    };

    const response = await postData(query, payload);

    if (response && response?.code === 200) {
      const bill_of_material_details = response?.data?.bill_of_material_details;
      setSelectedBom(bill_of_material_details);
    } else {
      HandleNotification("error", "bottomRight", "Error getting item data", null);
    }
  }, []);

  const onChangeInternalPO = (value, options) => {
    setOriginalPO(options?.original_po);
    setInternalPO(options?.internal_po);
    form.setFieldValue("original_po", options?.original_po);
  };

  const onChangeOriginalPO = (value, options) => {
    setOriginalPO(options?.original_po);
    setInternalPO(options?.internal_po);
    form.setFieldValue("internal_po", options?.internal_po);
  };

  const getItemDetailsData = useCallback(() => {
    const payload = {
      buyingId: styleDetails?.style?.buying_office_id,
      entryDate: form.getFieldValue("entry_date"),
      movementType: value,
      salesContractId: styleDetails?.style?.sales_contract_map_info?.sales_contract_id,
      styleId: styleDetails?.style?.id,
      supplierId: form.getFieldValue("supplier_id"),
    };

    const allKeyHasValue = Object.values(payload).every((value) => value || value === 0);

    if (allKeyHasValue) {
      BOMDetails({
        buying_office_id: styleDetails?.style?.buying_office_id,
        entry_date: form.getFieldValue("entry_date"),
        movement_type: value,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        style_id: styleDetails?.style?.id,
        supplier_id: form.getFieldValue("supplier_id"),
        use_fabric_interlining_store: "No",
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id || null,
      });
    }
  }, [BOMDetails, form, styleDetails, value]);

  useEffect(() => {
    getItemDetailsData();
  }, [styleDetails?.style?.id, form.getFieldValue("supplier_id"), form.getFieldValue("entry_date")]);

  const getInvoiceBySupplier = useCallback(
    async (supplierId) => {
      const query = `${INVOICE_BY_SUPPLIER}/${selectedSupplierId}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        setInvoiceList(response?.data?.data);
      }
    },
    [selectedSupplierId],
  );

  const getAllChallan = async (filterValues) => {
    const query = `${CHALLAN_LIST}`;
    const bodyData = {
      ...filterValues,
      per_page: 1000,
    };

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setChallanList(response?.data?.data?.data);
    } else {
      message.error("Error getting challan data");
    }
  };

  useEffect(() => {
    getAllChallan();
  }, []);

  useEffect(() => {
    if ((value === "receive_from_supplier" || value === "return_to_supplier") && selectedSupplierId) {
      getInvoiceBySupplier(selectedSupplierId);
    }
  }, [value, selectedSupplierId, getInvoiceBySupplier]);

  const onChangeMovement = (selected_movement_type) => {
    if (
      (selected_movement_type === "receive_from_supplier" || selected_movement_type === "return_to_supplier") &&
      !selectedSupplierId
    ) {
      HandleNotification("warning", "bottomRight", "Please select supplier", null);
    }

    setValue(selected_movement_type);

    if (form.getFieldValue("supplier_id")) {
      BOMDetails({
        buying_office_id: styleDetails?.style?.buying_office_id,
        entry_date: form.getFieldValue("entry_date"),
        movement_type: selected_movement_type,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        style_id: styleDetails?.style?.id,
        supplier_id: form.getFieldValue("supplier_id"),
        use_fabric_interlining_store: "No",
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id || null,
      });
    }
  };

  useEffect(() => {
    const supplierId = form.getFieldValue("supplier_id");
    const entryDate = form.getFieldValue("entry_date");

    const isSupplierRequired = value === "receive_from_supplier" || value === "return_to_supplier";

    if (styleDetails?.style?.id && entryDate && (!isSupplierRequired || supplierId)) {
      BOMDetails({
        buying_office_id: styleDetails?.style?.buying_office_id,
        entry_date: entryDate,
        movement_type: value,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        style_id: styleDetails?.style?.id,
        supplier_id: supplierId || null,
        use_fabric_interlining_store: "No",
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id || null,
      });
    }
  }, [
    styleDetails?.style?.id,
    styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
    styleDetails?.style?.buying_office_id,
    styleDetails?.style?.factory_info?.id,
    form,
    form.getFieldValue("supplier_id"),
    form.getFieldValue("entry_date"),
    value,
    BOMDetails,
  ]);

  const onFinish = (values) => {
    let updatedValues = {};
    updatedValues["movement_type"] = value;
    updatedValues["original_po"] = originalPO;
    updatedValues["internal_po"] = internalPO;

    const styleDetailsInfo = {
      sales_contract_id: styleDetails?.style?.sales_contract_map_info?.sales_contract_id,
      buying_office_id: styleDetails?.style?.buying_office_id,
      style_id: styleDetails?.style?.id,
    };

    updatedValues = {
      ...values,
      ...updatedValues,
      ...styleDetailsInfo,
    };

    delete updatedValues["balance_quantity"];
    delete updatedValues["movement_type_incoming"];
    delete updatedValues["received_total_quantity"];
    delete updatedValues["roll_number"];
    delete updatedValues["store_quantity"];

    dispatch(onCreateInventory(updatedValues));
    setEdit(!onEdit);
  };

  const checkRequiredFields = useCallback(() => {
    const values = form.getFieldsValue();

    if (!values.entry_date) return false;

    if (value === "receive_from_supplier" || value === "return_to_supplier") {
      if (!values.supplier_id) return false;

      if (selectedSupplierType === 0 || selectedSupplierType === 2) {
        if (!values.invoice_id) return false;
      }

      if (selectedSupplierType === 1) {
        if (!values.challan_id) return false;
      }
    }

    if (value?.includes("receive_from_sub_con_factory") && selectedSupplierType === 1) {
      if (!values.challan_id) return false;
    }

    if (value?.includes("issue")) {
      if (!values.requisition_no) return false;
    }

    if (["stock_sale", "send_to_buying_office", "return_to_supplier"].some((val) => value?.includes(val))) {
      if (!values.reference_no) return false;
    }

    if (!values.movement_type_incoming && !values.movement_type_outgoing) {
      return false;
    }

    return true;
  }, [form, value, selectedSupplierType]);

  const handleFormValuesChange = () => {
    setShowItemTable(checkRequiredFields());
  };

  useEffect(() => {
    setShowItemTable(checkRequiredFields());
  }, [checkRequiredFields]);

  console.log("showItemTable:", showItemTable);

  const extra = (
    <div className="extra-button-container">
      <Link to="/inventory/store/trims-store-entry" style={{ marginRight: 8 }}>
        <Button size="small">Back to list</Button>
      </Link>
      <Button size="small" htmlType="submit" onClick={() => form.resetFields()}>
        {" "}
        Reset
      </Button>
    </div>
  );

  return (
    <>
      <InventoryLayOutCommon>
        <AppPageHeader extra={extra} title={<Title level={5}>Trims and Accessories Entry</Title>} />
        <Content className="item-details">
          <Collapse defaultActiveKey={["1"]} style={{ marginTop: 15 }}>
            <Panel header={<b>Order Details</b>} key="1">
              <Form
                layout="vertical"
                onFinish={onFinish}
                form={form}
                onValuesChange={handleFormValuesChange}
                initialValues={{
                  buyer_name: selectedInventory?.data?.buyer_info?.name || "",
                  brand_name: selectedInventory?.data?.brand_info?.name || "",
                  style_id: selectedInventory?.data?.style_id || "",
                  buying_office_id: selectedInventory?.data?.buying_office_id || "",
                  sales_contract_id: selectedInventory?.data?.sales_contract_id || "",
                  supplier_id: null,
                }}
              >
                <InventoryForm
                  movement={value || ""}
                  styleDetails={styleDetails}
                  styleList={styleList}
                  dateFormat={dateFormat}
                  onChangeStyle={onChangeStyle}
                  onResetStyleChange={resetStyleDependentState}
                  onChangeInternalPO={onChangeInternalPO}
                  onChangeOriginalPO={onChangeOriginalPO}
                  onChangeMovement={onChangeMovement}
                  form={form}
                  setSelectedSupplierId={setSelectedSupplierId}
                  invoiceList={invoiceList}
                  challanList={challanList}
                  selectedSupplierType={selectedSupplierType}
                  setSelectedSupplierType={setSelectedSupplierType}
                  selectedSupplierId={selectedSupplierId}
                  getAllChallan={getAllChallan}
                />
              </Form>
            </Panel>
          </Collapse>
          {selectedBom && showItemTable && (
            <ItemTable
              selectedBOM={selectedBom}
              loading={loading}
              movement={value}
              styleDetails={styleDetails}
              form={form}
              getBOMDetails={BOMDetails}
            />
          )}
        </Content>
      </InventoryLayOutCommon>
    </>
  );
};

export default CreateTrimsInventory;
